import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import {
  type RatesDosageInterface,
  type NewTrialStateInterface,
  type PlotsValidationData,
  type ProtocolResponse,
} from "../types";
import {
  type AutozoneIntervalsInterface,
  type autozoneRequestStatus,
  type ProductivityZonesInterface,
  type VariabilityIndexDataInterface,
  type AutozoneOpacity,
  emptyAutozoneIntervalsObject,
  emptyProductivityZonesObject,
  variabilityIndexDataEmptyState,
  AutozoneColorPalette,
} from "../../autozones/types";
import { SoilColorPalette } from "../../../shared/sl/soilMapAnalysisTypes";

interface NewTrialStateContextValue {
  newTrialState: NewTrialStateInterface;
  setNewTrialState: React.Dispatch<React.SetStateAction<any>>;
  trialInfo: any;
  setTrialInfo: React.Dispatch<React.SetStateAction<any>>;
  ratesDosage: RatesDosageInterface;
  setRatesDosage: React.Dispatch<React.SetStateAction<RatesDosageInterface>>;
  activeKeys: any;
  setActiveKeys: any;
  trialProducts: any;
  setTrialProducts: React.Dispatch<React.SetStateAction<any>>;
  trialProtocolData: ProtocolResponse | null;
  setTrialProtocolData: any;
  ratesDosageValidation: boolean;
  setRatesDosageValidation: React.Dispatch<React.SetStateAction<any>>;
  ratesDosageValidationByType: {
    seed: boolean;
    biological: boolean;
    nutrient: boolean;
  };
  setRatesDosageValidationByType: React.Dispatch<
    React.SetStateAction<{
      seed: boolean;
      biological: boolean;
      nutrient: boolean;
    }>
  >;
  isStandardDataValid: boolean;
  setIsStandardDataValid: React.Dispatch<React.SetStateAction<any>>;
  plotsValidationData: any;
  setPlotsValidationData: any;
  current: number;
  setCurrent: React.Dispatch<React.SetStateAction<number>>;
  isFertilizerTabActive: boolean;
  setIsFertilizerTabActive: React.Dispatch<React.SetStateAction<boolean>>;
  activeRatesTab: string;
  setActiveRatesTab: React.Dispatch<React.SetStateAction<string>>;

  // Fertilizer products with full data including nutrients
  fertilizerProducts: any[];
  setFertilizerProducts: React.Dispatch<React.SetStateAction<any[]>>;

  resetStates: () => void;
  resetRatesDosageState: () => void;
  bioApplicationIndex: number;
  setBioApplicationIndex: React.Dispatch<React.SetStateAction<number>>;

  autozoneRequestStatus: autozoneRequestStatus;
  setAutozoneRequestStatus: React.Dispatch<React.SetStateAction<any>>;

  autozoneOpacity: AutozoneOpacity;
  setAutozoneOpacity: React.Dispatch<React.SetStateAction<AutozoneOpacity>>;
  autozoneColorPalette: AutozoneColorPalette;
  setAutozoneColorPalette: React.Dispatch<
    React.SetStateAction<AutozoneColorPalette>
  >;

  fieldOperationsData: any;
  setFieldOperationsData: React.Dispatch<React.SetStateAction<any>>;
  soilOperationsData: any;
  setSoilOperationsData: React.Dispatch<React.SetStateAction<any>>;
  soilColorPalette: SoilColorPalette;
  setSoilColorPalette: React.Dispatch<React.SetStateAction<SoilColorPalette>>;
  soilOpacity: number;
  setSoilOpacity: React.Dispatch<React.SetStateAction<any>>;
  autozonesData: {
    productivity_zones: ProductivityZonesInterface;
    variability_index: VariabilityIndexDataInterface;
    intervalsData?: AutozoneIntervalsInterface;
  };
  setAutozonesData: React.Dispatch<React.SetStateAction<any>>;

  // strips-flow : finalize state management flow for strips:
  // use stripsData here or move it to newTrialState

  stripsData: any;
  setStripsData: React.Dispatch<React.SetStateAction<any>>;
}

const NewTrialStateContext = createContext<NewTrialStateContextValue | null>(
  null
);

export const NewTrialStateProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [trialInfo, setTrialInfo] = useState<any>({
    field_ids: [""],
    id: undefined, // Explicitly set to undefined initially
  });
  const [newTrialState, setNewTrialState] = useState<NewTrialStateInterface>({
    ab_line: [],
    buffer_zone: [],
    machinery_grid: [],
    trial_plots: [],
  });

  const [ratesDosage, setRatesDosage] = useState<RatesDosageInterface>({
    rates_and_dosages: [],
    type: "",
    unit_of_rate: "",
    standard_seed_rate: 0,
    standard_product: "",
    standard_fertilisers: [],
  });
  const [current, setCurrent] = useState<number>(0);
  const [isFertilizerTabActive, setIsFertilizerTabActive] =
    useState<boolean>(false);
  const [activeRatesTab, setActiveRatesTab] = useState<string>("seed");

  const [ratesDosageValidation, setRatesDosageValidation] =
    useState<boolean>(false);
  const [ratesDosageValidationByType, setRatesDosageValidationByType] =
    useState<{
      seed: boolean;
      biological: boolean;
      nutrient: boolean;
    }>({
      seed: false,
      biological: false,
      nutrient: false,
    });
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [trialProducts, setTrialProducts] = useState<any>(null);
  const [trialProtocolData, setTrialProtocolData] =
    useState<ProtocolResponse | null>(null);
  const [isStandardDataValid, setIsStandardDataValid] =
    useState<boolean>(false);
  const [plotsValidationData, setPlotsValidationData] =
    useState<PlotsValidationData>({
      polygonsNotInsideField: [],
      intersectingPairs: [],
    });
  const [bioApplicationIndex, setBioApplicationIndex] = useState<number>(0);

  const [autozoneRequestStatus, setAutozoneRequestStatus] =
    useState<autozoneRequestStatus>("default");

  const [autozoneOpacity, setAutozoneOpacity] = useState<AutozoneOpacity>(60);

  const [autozoneColorPalette, setAutozoneColorPalette] =
    useState<AutozoneColorPalette>(
      AutozoneColorPalette.DEFAULT // or whatever default value you want
    );

  const [soilColorPalette, setSoilColorPalette] = useState<SoilColorPalette>(
    SoilColorPalette.DEFAULT
  );
  const [soilOpacity, setSoilOpacity] = useState<number>(60);

  const [fieldOperationsData, setFieldOperationsData] = useState<any>(null);
  const [soilOperationsData, setSoilOperationsData] = useState<any>(null);
  const [autozonesData, setAutozonesData] = useState<{
    productivity_zones: ProductivityZonesInterface;
    variability_index: VariabilityIndexDataInterface;
    intervalsData?: AutozoneIntervalsInterface;
  }>({
    productivity_zones: emptyProductivityZonesObject,
    variability_index: variabilityIndexDataEmptyState,
    intervalsData: emptyAutozoneIntervalsObject,
  });

  // New stripsData state
  const [stripsData, setStripsData] = useState<any>([{ gridIndex: 2 }]);

  // Fertilizer products with full data including nutrients
  const [fertilizerProducts, setFertilizerProducts] = useState<any[]>([]);

  const resetRatesDosageState = (): void => {
    setNewTrialState((previous) => ({
      ...previous,
      trial_plots: previous.trial_plots.map((entry) => ({
        ...entry,
        properties: {},
      })),
    }));
    setRatesDosage({
      rates_and_dosages: [],
      type: "",
      unit_of_rate: "",
      standard_seed_rate: 0,
      standard_product: "",
      standard_fertilisers: [],
    });
    setRatesDosageValidation(false);
    setRatesDosageValidationByType({
      seed: false,
      biological: false,
      nutrient: false,
    });
    setIsStandardDataValid(false);
    setActiveRatesTab("seed");
    setIsFertilizerTabActive(false);
    setActiveKeys([]);
    setTrialProducts(null);
    setFertilizerProducts([]);
    setBioApplicationIndex(0);
    setTrialProtocolData(null);
  };

  const resetStates = (): void => {
    setTrialInfo({ field_ids: [""] });
    setNewTrialState({
      ab_line: [],
      buffer_zone: [],
      machinery_grid: [],
      trial_plots: [],
    });
    setRatesDosage({
      rates_and_dosages: [],
      type: "",
      unit_of_rate: "",
      standard_seed_rate: 0,
      standard_product: "",
      standard_fertilisers: [],
    });
    setCurrent(0);
    setIsFertilizerTabActive(false);
    setRatesDosageValidation(false);
    setRatesDosageValidationByType({
      seed: false,
      biological: false,
      nutrient: false,
    });
    setActiveKeys([]);
    setTrialProducts(null);
    setTrialProtocolData(null);
    setIsStandardDataValid(false);
    setPlotsValidationData({
      polygonsNotInsideField: [],
      intersectingPairs: [],
    });
    setStripsData([{ gridLineIndex: 2 }]);
    // Optionally reset autozoneColorPalette if needed
    // setAutozoneColorPalette(AutozoneColorPalette.DEFAULT);
  };

  useEffect(() => {
    if (current !== 2) {
      setSoilOperationsData(null); // or undefined, depending on your default
    }
  }, [current]);

  const memoizedValue = useMemo(
    () => ({
      current,
      isFertilizerTabActive,
      setIsFertilizerTabActive,
      setCurrent,
      newTrialState,
      setNewTrialState,
      trialInfo,
      setTrialInfo,
      ratesDosage,
      setRatesDosage,
      activeKeys,
      setActiveKeys,
      trialProducts,
      setTrialProducts,
      trialProtocolData,
      setTrialProtocolData,
      ratesDosageValidation,
      setRatesDosageValidation,
      ratesDosageValidationByType,
      setRatesDosageValidationByType,
      isStandardDataValid,
      setIsStandardDataValid,
      plotsValidationData,
      setPlotsValidationData,
      activeRatesTab,
      setActiveRatesTab,
      fertilizerProducts,
      setFertilizerProducts,
      resetStates,
      resetRatesDosageState,
      bioApplicationIndex,
      setBioApplicationIndex,
      autozoneRequestStatus,
      setAutozoneRequestStatus,
      autozoneOpacity,
      setAutozoneOpacity,
      autozoneColorPalette,
      setAutozoneColorPalette,
      autozonesData,
      setAutozonesData,
      fieldOperationsData,
      setFieldOperationsData,
      stripsData,
      setStripsData,
      soilOperationsData,
      setSoilOperationsData,
      soilColorPalette,
      setSoilColorPalette,
      soilOpacity,
      setSoilOpacity,
    }),
    [
      current,
      isFertilizerTabActive,
      setIsFertilizerTabActive,
      setCurrent,
      newTrialState,
      setNewTrialState,
      trialInfo,
      setTrialInfo,
      ratesDosage,
      setRatesDosage,
      activeKeys,
      setActiveKeys,
      trialProducts,
      setTrialProducts,
      trialProtocolData,
      setTrialProtocolData,
      ratesDosageValidation,
      setRatesDosageValidation,
      ratesDosageValidationByType,
      setRatesDosageValidationByType,
      isStandardDataValid,
      setIsStandardDataValid,
      plotsValidationData,
      setPlotsValidationData,
      current,
      setCurrent,
      isFertilizerTabActive,
      setIsFertilizerTabActive,
      activeRatesTab,
      setActiveRatesTab,
      fertilizerProducts,
      setFertilizerProducts,
      resetStates,
      resetRatesDosageState,
      bioApplicationIndex,
      setBioApplicationIndex,
      autozoneRequestStatus,
      setAutozoneRequestStatus,
      autozoneOpacity,
      setAutozoneOpacity,
      autozoneColorPalette,
      setAutozoneColorPalette,
      autozonesData,
      setAutozonesData,
      fieldOperationsData,
      soilColorPalette,
      setSoilColorPalette,
      soilOpacity,
      setSoilOpacity,
      setFieldOperationsData,
      stripsData,
      setStripsData,
      soilOperationsData,
      setSoilOperationsData,
      soilOpacity,
      setSoilOpacity,
    ]
  );

  return (
    <NewTrialStateContext.Provider value={memoizedValue}>
      {children}
    </NewTrialStateContext.Provider>
  );
};

export const useNewTrialState = (): NewTrialStateContextValue => {
  const contextValue = useContext(NewTrialStateContext);
  if (!contextValue) {
    throw new Error(
      "useNewTrialState must be used within a NewTrialStateProvider"
    );
  }
  return contextValue;
};
